import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, switchMap, tap } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientAiService {
  private apiUrl = `api/ai-requests/patient`;
  private patientAIRequestsSubject = new BehaviorSubject<any[] | null>(null);
  private editingAIRequestSubject = new BehaviorSubject<any>(null);
  private incrementalUpdateSubject = new BehaviorSubject<any>(null);
  /**
   * M6 — every `aiRequestUpdated` payload, UNGATED.
   *
   * `incrementalUpdates$` below is gated on `editingAIRequestSubject`, i.e. it only ever emits
   * while the AI REQUEST DETAIL screen is open. That is correct for that screen and useless for
   * any other: `patient-note-detail` is a different route entirely, never sets the editing
   * request, and is exactly where a ward round's background detection results have to land
   * (tasks/risk-management.md M5/M6). Rather than widen the existing gate — which would start
   * feeding the request detail updates for requests it is not showing — this is a second,
   * unfiltered stream, and each subscriber filters on the id it cares about.
   *
   * A `Subject`, not a `BehaviorSubject`: these are DELTAS (`{ id, ...changedFields }`), and
   * replaying the last one to a late subscriber would hand a note the tail end of some other
   * request's update. Subscribers that need the current state fetch it — see
   * `getAIRequestByIdWithoutEditing`.
   */
  private aiRequestUpdateSubject = new Subject<any>();

  patientAIRequests$ = this.patientAIRequestsSubject.asObservable();
  editingAIRequest$ = this.editingAIRequestSubject.asObservable();
  incrementalUpdates$ = this.incrementalUpdateSubject.asObservable();
  aiRequestUpdates$ = this.aiRequestUpdateSubject.asObservable();

  patientId: string | null = null;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientId = patient.id;
          this.patientAIRequestsSubject.next([]);
          return this.loadAIRequestsByPatient(patient.id);
        } else {
          // If there's no current patient, clear the subject
          this.patientId = null;
          this.patientAIRequestsSubject.next([]);
          return []; // Return an empty array to satisfy the Observable return type
        }
      }),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared data
        const aiRequests = results ? results.data : [];
        this.patientAIRequestsSubject.next(aiRequests);
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {

   

    // Listener for when a new patientAiRequest is added
    this.socketService.listen('patientAiRequestAdded', 'patient-ai-service').subscribe(req => {
      if (req.patientId === this.patientId) {
        const current = this.patientAIRequestsSubject.getValue() || [];
        this.patientAIRequestsSubject.next([...current, req]);
      }
    });

    // Listener for when a patientAiRequest is updated
    this.socketService.listen('patientAiRequestUpdated', 'patient-ai-service').subscribe(req => {
      if (req.patientId === this.patientId) {
        const current = this.patientAIRequestsSubject.getValue() || [];
        const updated = current.map(item => item.id === req.id ? req : item);
        this.patientAIRequestsSubject.next(updated);
      }
    });

    // Listener for when a patientAiRequest is deleted
    this.socketService.listen('patientAiRequestDeleted', 'patient-ai-service').subscribe(req => {
      if (req.patientId === this.patientId) {
        const current = this.patientAIRequestsSubject.getValue() || [];
        const updated = current.filter(item => item.id !== req.id);
        this.patientAIRequestsSubject.next(updated);
      }
    });

    this.socketService.listen(`aiRequestUpdated`, 'patient-ai-service').subscribe(req => {
      // M6 — the ungated fan-out FIRST, before the editing gate below can `return`. A ward round
      // never opens the request detail (it is excluded from the patient-file request list by
      // design, B4, and navigates straight to `add-note`), so for the whole ward-round half of
      // this feature `editing` is always null and everything past the gate is dead code.
      this.aiRequestUpdateSubject.next(req);

      const editing = this.editingAIRequestSubject.getValue();
      if (!editing || req.id !== editing.id) return;


      /* RESPONSE PAYLOAD COULD INCLUDE ANY OF THE FOLLOWING:
     - suggestedProcedureCodes
     - suggestedModifiers
     - allergies
     - suggestedICD10Codes
     - detectedMedicalHistory
     - detectedMedications
     - suggestedMedication
     - suggestedAdminTasks
     - detectedProcedureTitle
     - detectedCondition
     - detectedRisks
     - motivationCodingBundleId
     - suggestedMotivationProcedureCodes
     - startTime
     - finishTime
     - codingBundleId
     */
      this.incrementalUpdateSubject.next(req);
    });

  }

  loadAIRequestsByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  addNoteFromAIRequest(noteData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/note`, noteData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getPatientAIRequestById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/request/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(tap(result => this.setEditingAIRequest(result?.data)));
  }

  /**
   * M7 — the same row `getPatientAIRequestById` fetches, WITHOUT taking over the editing request.
   *
   * `patient-note-detail` needs the true `backgroundDetectionStatus` on open, because a note
   * revisited after a page refresh has no socket history to reconstruct it from and D14 forbids
   * inferring it from whether the suggestion columns are NULL. But it must NOT call
   * `getPatientAIRequestById`: that method's `tap` pushes the row into `editingAIRequestSubject`,
   * which is the AI request detail's "this is the request I am editing" channel. Opening a ward
   * round note would silently install that request as the thing the request-detail screen edits,
   * and would start routing `incrementalUpdates$` to it.
   *
   * Same endpoint, same full row (`getPatientAIRequestById` in `patientAIRequestController.js`
   * does a bare `findOne({ where: { id } })` with no `attributes` narrowing, so every column is
   * present — the Phase-1 `hospitalId`-read-as-undefined bug was exactly a narrowed projection).
   * The only difference is the absent side effect.
   */
  getAIRequestByIdWithoutEditing(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/request/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getAIRequestStatus(id: string): Observable<{
    success: boolean;
    data: { id: string; hasNoteType: boolean; hasNotes: boolean; updatedAt: string };
  }> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/request/${id}/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  performUserPrompt(id: string, prompt: string, type: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/prompt`, { id, prompt, type }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  performUserDischargePrompt(id: string, admissionDate: any, dischargeDate: any, dischargeSummary: string, patientDischargeSummary: string, prompt: string, type: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/prompt/discharge`, { id, admissionDate, dischargeDate, dischargeSummary, patientDischargeSummary, prompt, type }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateNotes(id: string, noteTypeId: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/note`, { id, noteTypeId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generatePatientReport(id: string, selectedOptions: any[], noteTypeId: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/patient-report`, { id, selectedOptions, noteTypeId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateLetterOfMotivation(id: string, medicalAid: string, medicalAidNumber: string, medicalAidPlan: string, motivationReason: string, surgicalMotivationLetter: boolean, procedureCodes: any[], icdCodes: any[]) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/motivation-letter`, { id, medicalAid, medicalAidNumber, medicalAidPlan, procedureCodes, icdCodes, motivationReason, surgicalMotivationLetter }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateLetterToColleague(id: string, colleagueName: string, colleagueEmail: string, colleagueSpeciality: string, letterReason: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/letter-to-colleague`, { id, colleagueName, colleagueEmail, colleagueSpeciality, letterReason }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateRadiologyRequest(id: string, icdCodes: any[]) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/radiology-request`, { id, icdCodes }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateLetterToGP(id: string, generalPractitioner: string, generalPractitionerEmail: string, generalPractitionerPhone: string, type: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/letter-to-gp`, { id, generalPractitioner, generalPractitionerEmail, generalPractitionerPhone, type }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateLetterToPatient(id: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/letter-to-patient`, { id }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateLetterToPatientWork(id: string, employer: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/letter-to-patient-work`, { id, employer }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateDischargeSummary(id: string, admissionDate: any, dischargeDate: any, additionalData: any) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/discharge-summary`, { id, admissionDate, dischargeDate, additionalData }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateMorbidityMortality(id: string, admissionDate: any, dischargeDate: any, additionalData: any) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/morbidity-mortality`, { id, admissionDate, dischargeDate, additionalData }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  generateTaskList(id: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/task-list`, { id }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  generateProcedureCodeSuggestions(id: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/request/generate/procedure-codes`, { id }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateAIRequest(id: string, data: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/request/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteAIRequest(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/request/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingAIRequest(data: any) {
    this.editingAIRequestSubject.next(data);
  }


}
